import React from "react";
import {useSelector} from "react-redux";
import moment from "moment/moment";
import {getOrgWorkingDays,} from "../../utils/helperUtils";
import {
  GT_bottomSpacing,
  GT_progressBarAndTasksGapInPx,
  GT_progressBarTopInPx,
  GT_taskCardHeight,
  GT_tasksTopInPx
} from "./index";
import {DateInfo, Holiday, ViewType} from "./types";
import {generateVisibleDates} from "./utils/dateUtils";
import {
  CurrentDateFlag,
  DateIndicatorFlag,
  DayHeader,
  GridLine,
  HeaderSection,
  HolidayFlag,
  Milestone,
  MilestoneFlag,
  StartDateFlag,
  EndDateFlag
} from "./components/GridComponents";
import YearlyGridView from "./components/YearlyGridView";
import QuarterlyGridView from "./components/QuarterlyGridView";
import {useTimelineMouseTracking} from "./hooks/useTimelineMouseTracking";
import {ENTITY} from "../../redux/slices/timeline";

interface GTGridLayoutProps {
  viewType: ViewType;
  orgId: string;
  entityLength: number;
  spacing: number;
  startDate: string;
  endDate: string;
  milestones?: Milestone[];
}

const GTGridLayout: React.FC<GTGridLayoutProps> = ({ viewType, orgId, entityLength, startDate, endDate, spacing, milestones }): React.ReactElement => {
  const {
    orgSchedule: organizationHolidays = [],
  } = useSelector((state: any) => state.organization);

  const {
    currentEntity,
  } = useSelector((state: any) => state.timeline);

  const totalHeight = GT_progressBarTopInPx
    + GT_progressBarAndTasksGapInPx
    + GT_tasksTopInPx
    + entityLength * GT_taskCardHeight
    + GT_bottomSpacing;

  const organizationWorkingDays = getOrgWorkingDays(orgId || '');
  const holidayDates = new Set(
    organizationHolidays.map((h: Holiday) => (h?.date || '').split('T')[0])
  );
  const isWorkingDay = (d: string) => {
    const dow = moment(d).format('YYYY-MM-DD');
    const weekend = !organizationWorkingDays.includes(moment(dow).day());
    const isHoliday = holidayDates.has(dow);
    return !weekend && !isHoliday;
  };
  const getNextWorkingDay = (d: string) => {
    let cur = moment(d).add(1, 'day');
    while (true) {
      const ds = cur.format('YYYY-MM-DD');
      if (isWorkingDay(ds)) return ds;
      cur = cur.add(1, 'day');
    }
  };

  const visibleDates = generateVisibleDates(
    startDate,
    endDate,
    viewType,
    organizationHolidays,
    organizationWorkingDays
  );

  const { isMouseInTimeline, mousePosition, topPosition, mouseHandlers } = useTimelineMouseTracking({
    dates: visibleDates,
    spacing,
    viewType
  });

  const datesByYearAndMonth = visibleDates.reduce((acc: { [key: string]: DateInfo[] }, date) => {
    const monthYear = moment(date.date).format('MMMM YYYY');

    if (!acc[monthYear]) {
      acc[monthYear] = [];
    }
    acc[monthYear].push(date);

    return acc;
  }, {});

  if (!startDate || !endDate) {
    return (
      <div className="absolute top-0 h-[100%]" style={{
        height: `max(${GT_progressBarTopInPx + GT_progressBarAndTasksGapInPx + GT_tasksTopInPx + GT_bottomSpacing}px, calc(100vh - 119px))`,
        minHeight: '400px'
      }} />
    );
  }

  if (viewType === 'yearly') {
    return (
      <YearlyGridView
        startDate={startDate}
        endDate={endDate}
        spacing={spacing}
        totalHeight={totalHeight}
        milestones={milestones}
      />
    );
  }

  if (viewType === 'quarterly') {
    return (
      <QuarterlyGridView
        startDate={startDate}
        endDate={endDate}
        spacing={spacing}
        totalHeight={totalHeight}
        orgId={orgId}
        milestones={milestones}
      />
    );
  }

  return (
    <div className="absolute top-0" style={{ height: `max(${totalHeight}px, 100%)` }}>
      <div className="sticky top-0 z-40 bg-gray-50 dark:bg-[#111928]">
        {/* Months */}
        <div className="flex bg-white dark:bg-[#1a1f2e]">
          {Object.entries(datesByYearAndMonth).map(([monthYear, dates], monthIndex) => {
            const monthWidth = dates.length * spacing;
            return (
              <HeaderSection
                key={monthYear}
                label={monthYear}
                width={monthWidth}
                index={monthIndex}
              />
            );
          })}
        </div>

        {/* Days */}
        <div className="flex bg-white dark:bg-[#1a1f2e]">
          {visibleDates.map((date) => (
            <DayHeader
              key={date.date}
              date={date.date}
              spacing={spacing}
              viewType={viewType}
            />
          ))}
        </div>
      </div>

      {/* Grid lines */}
      <div 
        className="absolute top-0 left-0 w-full"
        style={{ height: `max(${totalHeight}px, 100%)` }}
        {...mouseHandlers}
      >
        {visibleDates.map((date, index) => {
          const milestonesForDate = milestones?.filter(milestone => {
            const milestoneDate = moment(milestone.start_date).format('YYYY-MM-DD');
            return milestoneDate === moment(date.date).format('YYYY-MM-DD');
          }) || [];

          const isToday = moment(date.date).format('YYYY-MM-DD') === moment().format('YYYY-MM-DD');
          const isStart = moment(date.date).format('YYYY-MM-DD') === moment(startDate).format('YYYY-MM-DD');
          const endFlagTargetDate = getNextWorkingDay(endDate);
          const isEnd = moment(date.date).format('YYYY-MM-DD') === endFlagTargetDate;

         
          return (
            <div key={`grid-${date.date}`}>
              <GridLine
                left={index * spacing}
                width={spacing}
                height="100%"
                isWeekend={date.nextDayIsWeekend}
                viewType={viewType as ViewType}
              />

              {date.nextDayIsHoliday && (
                <HolidayFlag
                  holidays={date.next_holiday_details || []}
                  date={date.date}
                  viewType={viewType}
                  position={{
                    right: `${((visibleDates.length - 1) - index) * spacing}px`,
                    top: (viewType === 'weekly') ? '81px' : '72px',
                  }}
                  transform="translateX(-100%)"
                />
              )}

              {currentEntity === ENTITY.ROADMAP && milestonesForDate.map((milestone, milestoneIndex) => (
                <MilestoneFlag
                  key={`milestone-${milestone.id}-${milestoneIndex}`}
                  milestone={milestone}
                  position={{
                    left: `${index * spacing}px`,
                    top: (viewType === 'weekly') ? '81px' : '71px'
                  }}
                />
              ))}

              {isStart && (
                <StartDateFlag
                  date={startDate}
                  position={{
                    left: `${index * spacing}px`,
                    top: (viewType === 'weekly') ? '75.5px' : '65.5px'
                  }}
                />
              )}

              {isToday && (
                <CurrentDateFlag
                  position={{
                    left: `${index * spacing}px`,
                    top: (viewType === 'weekly') ? '75.5px' : '65.5px'
                  }}
                />
              )}

              {isEnd && (
                <EndDateFlag
                  originalDate={endDate}
                  position={{
                    left: `${index * spacing}px`,
                    top: (viewType === 'weekly') ? '81px' : '71px'
                  }}
                />
              )}

              {isMouseInTimeline && mousePosition && (
                <DateIndicatorFlag
                  date={mousePosition.date}
                  left={mousePosition.x}
                  viewType={viewType}
                  top={topPosition}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default GTGridLayout;

